import { useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  EMPLOYEE_EXPORT_PRESETS,
  exportEmployeesWithPreset,
} from "@/lib/exportUtils";
import { applyFullSsns, fetchFullSsns } from "@/lib/ssnReveal";

/**
 * Export dialog for employee rosters (Phase 10): preset radio + column
 * preview, replacing the one-shot Export button. `employees` should be the
 * FILTERED/SORTED list the table currently shows, so the export matches what
 * the user sees. Dependents are fetched here, only when the chosen preset
 * needs them.
 */
export function EmployeeExportDialog({
  employees,
  filenameBase,
}: {
  employees: any[];
  filenameBase: string;
}) {
  const [open, setOpen] = useState(false);
  const [presetKey, setPresetKey] = useState("basic");
  const [isExporting, setIsExporting] = useState(false);

  const preset =
    EMPLOYEE_EXPORT_PRESETS.find((p) => p.key === presetKey) ??
    EMPLOYEE_EXPORT_PRESETS[0];

  const handleExport = async () => {
    setIsExporting(true);
    try {
      let rows = employees;
      // The dependent presets (census/full) carry SSNs; list rows are masked,
      // so the full values come from the audited reveal endpoint.
      if (preset.includeDependents) {
        rows = await Promise.all(
          employees.map(async (emp: any) => {
            let dependents: any[] = [];
            try {
              const res = await fetch(`/api/employees/${emp.id}/dependents`, {
                credentials: "include",
              });
              const body = res.ok ? await res.json() : [];
              dependents = Array.isArray(body) ? body : [];
            } catch {
              // export the employee without dependents
            }
            try {
              const merged = applyFullSsns(emp, dependents, await fetchFullSsns(emp.id, "export"));
              return { ...merged.employee, dependents: merged.dependents };
            } catch {
              return { ...emp, dependents };
            }
          }),
        );
      }
      exportEmployeesWithPreset(rows, preset, `${filenameBase}_${preset.key}`);
      setOpen(false);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Export Employees</DialogTitle>
          <DialogDescription>
            {employees.length} employee{employees.length === 1 ? "" : "s"} in the
            current view will be exported (search and status filters apply).
          </DialogDescription>
        </DialogHeader>
        <RadioGroup value={presetKey} onValueChange={setPresetKey} className="space-y-2">
          {EMPLOYEE_EXPORT_PRESETS.map((p) => (
            <div
              key={p.key}
              className={`rounded-md border p-3 ${p.key === presetKey ? "border-primary bg-primary/5" : ""}`}
            >
              <div className="flex items-start gap-2">
                <RadioGroupItem value={p.key} id={`export-preset-${p.key}`} className="mt-0.5" />
                <div className="min-w-0">
                  <Label htmlFor={`export-preset-${p.key}`} className="font-medium cursor-pointer">
                    {p.label}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>
                </div>
              </div>
            </div>
          ))}
        </RadioGroup>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
            Columns
          </p>
          <p className="text-xs text-muted-foreground break-words">
            {preset.fields.map((f) => f.header).join(", ")}
            {preset.includeDependents ? ", dependents 1–5 (name, relationship, DOB, SSN, sex)" : ""}
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={isExporting || employees.length === 0}>
            {isExporting ? "Exporting…" : "Export CSV"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
